import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import {
  Loader2,
  RefreshCw,
  Eye,
  Search,
  Image,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  Link,
  FileText,
  History,
  Trash2,
  Plus
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { API_URL } from '../../config/api';
import { ReprocessingDialog } from '../reprocessing/ReprocessingDialog';
import { Checkbox } from '../ui/checkbox';
import { AddUrlDialog } from './AddUrlDialog';

interface Page {
  id: string;
  url: string;
  title: string;
  thumbnail_url?: string;
  screenshot_url?: string;
  status: 'active' | 'analyzing' | 'error' | 'inactive';
  error_message?: string;
  first_crawled_at: string;
  last_analyzed_at: string;
  analysis_count: number;
  has_semantic_data: boolean;
  has_content: boolean;
  created_at: string;
  updated_at: string;
}

interface PageDetails extends Page {
  site_name?: string;
  site_domain?: string;
  page_semantic?: any;
  page_content_text?: string;
  main_anchors?: Array<{
    linkText?: string;
    text?: string;
    url?: string; 
    href?: string;
    semanticValue?: number;
    count?: number;
    sourceUrl?: string;
    discovered?: any;
  }>;
  headers?: Array<{tag: string, text: string, level: number}> | Record<string, any>;
  metadata?: any;
  analysis_history?: Array<{
    id: number;
    analysis_type: string;
    triggered_by: string;
    status: string;
    created_at: string;
    completed_at?: string;
    duration_ms?: number;
  }>;
}

interface Props {
  siteId: string;
}

// Helper to convert backend URLs to frontend-accessible URLs
const fixScreenshotUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  // Replace backend:3001 with /api which gets proxied correctly
  return url.replace('http://backend:3001', '/api');
};

export const PageManager: React.FC<Props> = ({ siteId }) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState('last_analyzed_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [batchRefreshing, setBatchRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reprocessingDialogOpen, setReprocessingDialogOpen] = useState(false);
  const [reprocessingPageIds, setReprocessingPageIds] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [addUrlDialogOpen, setAddUrlDialogOpen] = useState(false);
  const [siteUrl, setSiteUrl] = useState<string>('');

  useEffect(() => {
    fetchPages();
    fetchSiteInfo();
  }, [siteId, page, statusFilter, sortBy, sortOrder]);

  const fetchPages = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sortBy,
        sortOrder,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await fetch(`${API_URL}/page-manager/sites/${siteId}/pages?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch pages');
      
      const data = await response.json();
      setPages(data.pages);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      setError('Failed to load pages');
      console.error('Error fetching pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/sites/${siteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSiteUrl(data.url || '');
      }
    } catch (err) {
      console.error('Error fetching site info:', err);
    }
  };

  const fetchPageDetails = async (pageId: string) => {
    try {
      const response = await fetch(`${API_URL}/page-manager/pages/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch page details');
      
      const data = await response.json();
      console.log('Page details received:', data);
      console.log('Main anchors:', data.main_anchors);
      console.log('Headers:', data.headers);
      setSelectedPage(data);
      setDetailsOpen(true);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load page details',
        variant: 'destructive',
      });
      console.error('Error fetching page details:', err);
    }
  };

  const triggerAnalysis = async (pageId: string, analysisType: string = 'full') => {
    setRefreshing(pageId);
    try {
      const response = await fetch(`${API_URL}/page-manager/pages/${pageId}/analyze`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ analysisType })
      });
      
      if (!response.ok) throw new Error('Failed to trigger analysis');
      
      toast({
        title: 'Analysis Started',
        description: 'Page analysis has been triggered',
      });
      
      // Refresh the page list after a delay
      setTimeout(() => {
        fetchPages();
        setRefreshing(null);
      }, 2000);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to trigger analysis',
        variant: 'destructive',
      });
      console.error('Error triggering analysis:', err);
      setRefreshing(null);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchPages();
  };

  const handleDeletePage = async () => {
    if (!pageToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/page-manager/pages/${pageToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to delete page');
      
      toast({
        title: 'Page Deleted',
        description: `Page "${pageToDelete.title || pageToDelete.url}" has been removed successfully.`,
      });
      
      // Refresh the page list
      fetchPages();
      setDeleteConfirmOpen(false);
      setPageToDelete(null);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to delete page. Please try again.',
        variant: 'destructive',
      });
      console.error('Error deleting page:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleBatchReprocess = async () => {
    // Check if already reprocessing
    if (isReprocessing) {
      toast({
        title: 'Reprocessing in Progress',
        description: 'Please wait for the current reprocessing to complete',
        variant: 'default',
      });
      return;
    }

    setBatchRefreshing(true);
    setIsReprocessing(true);
    
    try {
      let pageIdsToReprocess: string[] = [];
      
      if (selectedPages.size > 0) {
        // Use selected pages
        pageIdsToReprocess = Array.from(selectedPages);
      } else {
      // First, get all page IDs for this site
      const response = await fetch(`${API_URL}/page-manager/sites/${siteId}/pages?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch pages');
      
      const data = await response.json();
      pageIdsToReprocess = data.pages?.map((p: Page) => p.id) || [];
      }
      
      if (pageIdsToReprocess.length === 0) {
        toast({
          title: 'No pages found',
          description: 'This site has no pages to reprocess',
          variant: 'default',
        });
        setBatchRefreshing(false);
        return;
      }
      
      // Trigger complete reprocessing
      console.log('[PageManager] Triggering reprocessing for pages:', pageIdsToReprocess);
      console.log('[PageManager] API URL:', `${API_URL}/page-reprocessing-simple/reprocess-simple`);
      
      const reprocessResponse = await fetch(`${API_URL}/page-reprocessing-simple/reprocess-simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ 
          pageIds: pageIdsToReprocess,
          siteId
        })
      });
      
      console.log('[PageManager] Response status:', reprocessResponse.status);
      
      if (!reprocessResponse.ok) {
        const errorText = await reprocessResponse.text();
        console.error('[PageManager] Reprocessing error:', errorText);
        throw new Error(`Failed to trigger reprocessing: ${errorText}`);
      }
      
      const result = await reprocessResponse.json();
      console.log('[PageManager] Reprocessing result:', result);
      
      // Show reprocessing dialog
      setReprocessingPageIds(pageIdsToReprocess);
      setReprocessingDialogOpen(true);
      
      toast({
        title: 'Reprocessing Started',
        description: `Started reprocessing for ${result.pagesTriggered} pages`,
      });
      
      // Refresh the page list after a delay
      setTimeout(() => {
        fetchPages();
      }, 3000);
      
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to trigger page reprocessing',
        variant: 'destructive',
      });
      console.error('Error triggering reprocessing:', err);
      setIsReprocessing(false);
    } finally {
      setBatchRefreshing(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedPages.size === pages.length) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set(pages.map(p => p.id)));
    }
  };

  const handleSelectPage = (pageId: string) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageId)) {
      newSelected.delete(pageId);
    } else {
      newSelected.add(pageId);
    }
    setSelectedPages(newSelected);
  };

  const handleReprocessComplete = () => {
    setIsReprocessing(false);
    setSelectedPages(new Set());
    fetchPages();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'analyzing': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'active': return 'default';
      case 'analyzing': return 'secondary';
      case 'error': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Page Manager</CardTitle>
              <CardDescription>View and manage all pages for this site</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setAddUrlDialogOpen(true)}
                title="Add a new URL for complete analysis"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add URL
              </Button>
              <Button
                variant="outline"
                onClick={handleBatchReprocess}
                disabled={batchRefreshing || isReprocessing}
                title={selectedPages.size > 0 
                  ? `Reprocess ${selectedPages.size} selected pages` 
                  : "Reprocess all pages with real-time progress monitoring"}
              >
                {batchRefreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {selectedPages.size > 0 
                      ? `Reprocess ${selectedPages.size} Selected` 
                      : 'Reprocess All Pages'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filters */}
            <div className="grid gap-4 md:grid-cols-5">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search pages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-8"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="analyzing">Analyzing</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_analyzed_at">Last Analyzed</SelectItem>
                  <SelectItem value="first_crawled_at">First Crawled</SelectItem>
                  <SelectItem value="analysis_count">Analysis Count</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              >
                {sortOrder === 'ASC' ? '↑ Ascending' : '↓ Descending'}
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Pages Table */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={selectedPages.size === pages.length && pages.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-[100px]">Thumbnail</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Analyzed</TableHead>
                      <TableHead className="text-center">Analysis</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          No pages found
                        </TableCell>
                      </TableRow>
                    ) : (
                      pages.map((page) => (
                        <TableRow key={page.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedPages.has(page.id)}
                              onCheckedChange={() => handleSelectPage(page.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Avatar className="h-12 w-16 rounded">
                              <AvatarImage 
                                src={fixScreenshotUrl(page.thumbnail_url) || fixScreenshotUrl(page.screenshot_url)} 
                                alt={page.title}
                                className="object-cover"
                              />
                              <AvatarFallback className="rounded">
                                <Image className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>
                          
                          <TableCell>
                            <div className="max-w-[300px] truncate" title={page.url}>
                              {(() => {
                                try {
                                  const url = new URL(page.url);
                                  return url.pathname + url.search + url.hash;
                                } catch {
                                  // If URL parsing fails, try to extract path manually
                                  const match = page.url.match(/^https?:\/\/[^\/]+(\/.*)?$/);
                                  return match && match[1] ? match[1] : page.url;
                                }
                              })()}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="max-w-[200px] truncate" title={page.title || 'No title'}>
                              {page.title || 'No title'}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <Badge variant={getStatusVariant(page.status)}>
                              <span className="flex items-center gap-1">
                                {getStatusIcon(page.status)}
                                {page.status}
                              </span>
                            </Badge>
                          </TableCell>
                          
                          <TableCell>
                            <div className="text-sm" title={new Date(page.last_analyzed_at).toLocaleString()}>
                              {formatDistanceToNow(new Date(page.last_analyzed_at), { addSuffix: true })}
                            </div>
                          </TableCell>
                          
                          <TableCell className="text-center">
                            {page.analysis_count}
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex gap-1">
                              {page.has_semantic_data && (
                                <Badge variant="outline" className="text-xs">S</Badge>
                              )}
                              {page.has_content && (
                                <Badge variant="outline" className="text-xs">C</Badge>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => fetchPageDetails(page.id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => triggerAnalysis(page.id)}
                                disabled={refreshing === page.id}
                              >
                                {refreshing === page.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                              
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setPageToDelete(page);
                                  setDeleteConfirmOpen(true);
                                }}
                                className="hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Page Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          {selectedPage && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">Page Details</DialogTitle>
                <DialogDescription className="text-sm">
                  {selectedPage.url}
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="overview" className="flex-1">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="semantic">Semantic</TabsTrigger>
                  <TabsTrigger value="links">Links</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>
                
                <ScrollArea className="h-[600px] w-full rounded-md">
                  <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Page Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div>
                            <Label className="text-muted-foreground">Title</Label>
                            <p className="font-medium">{selectedPage.title || 'No title'}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Status</Label>
                            <Badge variant={getStatusVariant(selectedPage.status)}>
                              {selectedPage.status}
                            </Badge>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">First Crawled</Label>
                            <p>{new Date(selectedPage.first_crawled_at).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Last Analyzed</Label>
                            <p>{formatDistanceToNow(new Date(selectedPage.last_analyzed_at), { addSuffix: true })}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Analysis Count</Label>
                            <p>{selectedPage.analysis_count}</p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {selectedPage.screenshot_url && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Screenshot</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <img
                              src={fixScreenshotUrl(selectedPage.screenshot_url)}
                              alt="Page screenshot"
                              className="w-full rounded-md border"
                            />
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="content" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Page Content</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-md bg-muted p-4">
                          <pre className="whitespace-pre-wrap text-sm">
                            {selectedPage.page_content_text || 'No content available'}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="semantic" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Semantic Analysis</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedPage.page_semantic ? (
                          <div className="space-y-4">
                            {(() => {
                              // Parse semantic data if it's a string
                              let semanticData = selectedPage.page_semantic;
                              if (typeof semanticData === 'string') {
                                try {
                                  semanticData = JSON.parse(semanticData);
                                } catch (e) {
                                  // If parsing fails, show as plain text
                                  return (
                                    <div className="rounded-md bg-muted p-4">
                                      <p className="text-sm whitespace-pre-wrap break-words">
                                        {selectedPage.page_semantic}
                                      </p>
                                    </div>
                                  );
                                }
                              }
                              
                              // Organized display of semantic data
                              return (
                                <div className="space-y-4">
                                  {/* One-liner summary */}
                                  {semanticData.oneLiner && (
                                    <div>
                                      <h4 className="font-medium text-sm mb-2">Summary</h4>
                                      <div className="rounded-md bg-muted p-3">
                                        <p className="text-sm italic">{semanticData.oneLiner.replace(/['"]/g, '')}</p>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Page Role & Category */}
                                  {(semanticData.pageRole || semanticData.semanticCategory || semanticData.category) && (
                                    <div className="grid gap-4 md:grid-cols-2">
                                      {semanticData.pageRole && (
                                        <div>
                                          <h4 className="font-medium text-sm mb-2">Page Role</h4>
                                          <Badge variant="secondary" className="text-sm">
                                            {semanticData.pageRole.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </Badge>
                                        </div>
                                      )}
                                      {semanticData.semanticCategory && (
                                        <div>
                                          <h4 className="font-medium text-sm mb-2">Category</h4>
                                          <Badge variant="outline" className="text-sm">
                                            {semanticData.semanticCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </Badge>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Enhanced Product/Service Details */}
                                  {(semanticData.category || semanticData.subcategories || semanticData.brand || semanticData.itemName || semanticData.itemType || semanticData.productInfo || semanticData.characteristics || semanticData.pricing || semanticData.mainImage || semanticData.additionalImages) && (
                                    <div className="space-y-4">
                                      <h4 className="font-medium text-sm mb-2">Product/Service Details</h4>
                                      
                                      {/* Basic Product Info */}
                                      <div className="grid gap-3">
                                        {semanticData.itemName && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Name:</span>
                                            <span className="text-sm font-medium">{semanticData.itemName}</span>
                                          </div>
                                        )}
                                        {semanticData.itemType && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Type:</span>
                                            <Badge variant="outline" className="text-sm capitalize">
                                              {semanticData.itemType}
                                            </Badge>
                                          </div>
                                        )}
                                        {semanticData.category && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Category:</span>
                                            <Badge variant="default" className="text-sm">
                                              {semanticData.category}
                                            </Badge>
                                          </div>
                                        )}
                                        {semanticData.brand && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Brand:</span>
                                            <Badge variant="secondary" className="text-sm">
                                              {semanticData.brand}
                                            </Badge>
                                          </div>
                                        )}
                                        {semanticData.subcategories && semanticData.subcategories.length > 0 && (
                                          <div>
                                            <span className="text-sm text-muted-foreground">Subcategories:</span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {semanticData.subcategories.map((subcat: string, idx: number) => (
                                                <Badge key={idx} variant="outline" className="text-xs">
                                                  {subcat}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Product Info from productInfo object */}
                                      {semanticData.productInfo && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Product Information</h5>
                                          <div className="grid gap-2">
                                            {semanticData.productInfo.productName && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Product Name:</span>
                                                <span className="text-sm">{semanticData.productInfo.productName}</span>
                                              </div>
                                            )}
                                            {semanticData.productInfo.productCategory && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Product Category:</span>
                                                <Badge variant="outline" className="text-xs">
                                                  {semanticData.productInfo.productCategory}
                                                </Badge>
                                              </div>
                                            )}
                                            {semanticData.productInfo.sku && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">SKU:</span>
                                                <code className="text-xs bg-muted px-1 rounded">{semanticData.productInfo.sku}</code>
                                              </div>
                                            )}
                                            {semanticData.productInfo.brand && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Brand:</span>
                                                <Badge variant="secondary" className="text-xs">
                                                  {semanticData.productInfo.brand}
                                                </Badge>
                                              </div>
                                            )}
                                            {semanticData.productInfo.availability && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Availability:</span>
                                                <Badge 
                                                  variant={semanticData.productInfo.availability === 'in_stock' ? 'default' : 'destructive'} 
                                                  className="text-xs"
                                                >
                                                  {semanticData.productInfo.availability.replace('_', ' ')}
                                                </Badge>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Pricing Information */}
                                      {(semanticData.pricing || (semanticData.characteristics && semanticData.characteristics.price)) && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pricing</h5>
                                          <div className="grid gap-2">
                                            {semanticData.pricing?.price && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Price:</span>
                                                <span className="text-sm font-medium">
                                                  {semanticData.pricing.currency && semanticData.pricing.currency !== 'USD' ? semanticData.pricing.currency : '$'}{semanticData.pricing.price}
                                                </span>
                                              </div>
                                            )}
                                            {semanticData.pricing?.priceText && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Display:</span>
                                                <span className="text-sm">{semanticData.pricing.priceText}</span>
                                              </div>
                                            )}
                                            {semanticData.pricing?.salePrice && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Sale Price:</span>
                                                <span className="text-sm font-medium text-green-600">
                                                  {semanticData.pricing.currency && semanticData.pricing.currency !== 'USD' ? semanticData.pricing.currency : '$'}{semanticData.pricing.salePrice}
                                                </span>
                                              </div>
                                            )}
                                            {semanticData.pricing?.priceType && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Price Type:</span>
                                                <Badge variant="outline" className="text-xs">
                                                  {semanticData.pricing.priceType.replace('_', ' ')}
                                                </Badge>
                                              </div>
                                            )}
                                            {semanticData.pricing?.priceRange && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Price Range:</span>
                                                <span className="text-sm">
                                                  {semanticData.pricing.currency && semanticData.pricing.currency !== 'USD' ? semanticData.pricing.currency : '$'}{semanticData.pricing.priceRange.min} - {semanticData.pricing.currency && semanticData.pricing.currency !== 'USD' ? semanticData.pricing.currency : '$'}{semanticData.pricing.priceRange.max}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Availability Status */}
                                      {(semanticData.characteristics?.availability || semanticData.productInfo?.availability) && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Availability</h5>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">Status:</span>
                                            <Badge 
                                              variant={
                                                (semanticData.characteristics?.availability || semanticData.productInfo?.availability) === 'in_stock' 
                                                  ? 'default' 
                                                  : (semanticData.characteristics?.availability || semanticData.productInfo?.availability) === 'limited' 
                                                    ? 'secondary' 
                                                    : 'destructive'
                                              } 
                                              className="text-xs"
                                            >
                                              {(semanticData.characteristics?.availability || semanticData.productInfo?.availability).replace('_', ' ')}
                                            </Badge>
                                          </div>
                                        </div>
                                      )}

                                      {/* Ratings & Reviews */}
                                      {semanticData.characteristics?.rating && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ratings & Reviews</h5>
                                          <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-muted-foreground">Rating:</span>
                                              <div className="flex items-center gap-1">
                                                <span className="text-sm font-medium">{semanticData.characteristics.rating.value}</span>
                                                <span className="text-xs text-muted-foreground">/ 5</span>
                                              </div>
                                            </div>
                                            {semanticData.characteristics.rating.count && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Reviews:</span>
                                                <span className="text-sm">{semanticData.characteristics.rating.count}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Physical Characteristics */}
                                      {semanticData.characteristics?.physical && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Physical Characteristics</h5>
                                          <div className="grid gap-2">
                                            {semanticData.characteristics.physical.size && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Size:</span>
                                                <span className="text-sm">
                                                  {Array.isArray(semanticData.characteristics.physical.size) 
                                                    ? semanticData.characteristics.physical.size.join(', ') 
                                                    : semanticData.characteristics.physical.size}
                                                </span>
                                              </div>
                                            )}
                                            {semanticData.characteristics.physical.color && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Color:</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {Array.isArray(semanticData.characteristics.physical.color) 
                                                    ? semanticData.characteristics.physical.color.map((color: string, idx: number) => (
                                                        <Badge key={idx} variant="outline" className="text-xs">
                                                          {color}
                                                        </Badge>
                                                      ))
                                                    : <Badge variant="outline" className="text-xs">
                                                        {semanticData.characteristics.physical.color}
                                                      </Badge>
                                                  }
                                                </div>
                                              </div>
                                            )}
                                            {semanticData.characteristics.physical.weight && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Weight:</span>
                                                <span className="text-sm">{semanticData.characteristics.physical.weight}</span>
                                              </div>
                                            )}
                                            {semanticData.characteristics.physical.material && (
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Material:</span>
                                                <span className="text-sm">
                                                  {Array.isArray(semanticData.characteristics.physical.material) 
                                                    ? semanticData.characteristics.physical.material.join(', ') 
                                                    : semanticData.characteristics.physical.material}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Features */}
                                      {semanticData.characteristics?.features && semanticData.characteristics.features.length > 0 && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Features</h5>
                                          <div className="space-y-1">
                                            {semanticData.characteristics.features.map((feature: string, idx: number) => (
                                              <div key={idx} className="flex items-center gap-2">
                                                <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm">{feature}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Technical Specifications */}
                                      {semanticData.characteristics?.specifications && Object.keys(semanticData.characteristics.specifications).length > 0 && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technical Specifications</h5>
                                          <div className="space-y-1">
                                            {Object.entries(semanticData.characteristics.specifications).map(([key, value], idx) => (
                                              <div key={idx} className="flex items-center justify-between text-sm">
                                                <span className="text-muted-foreground">{key}:</span>
                                                <span className="font-medium">{String(value)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Product Images */}
                                      {(semanticData.mainImage || (semanticData.additionalImages && semanticData.additionalImages.length > 0)) && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Images</h5>
                                          <div className="space-y-3">
                                            {semanticData.mainImage && (
                                              <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs text-muted-foreground">Main Image:</span>
                                                  {semanticData.mainImage.isProductImage && (
                                                    <Badge variant="default" className="text-xs">Product</Badge>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <img 
                                                    src={semanticData.mainImage.url} 
                                                    alt={semanticData.mainImage.alt || 'Product image'} 
                                                    className="w-16 h-16 object-cover rounded border"
                                                    onError={(e) => {
                                                      e.currentTarget.style.display = 'none';
                                                    }}
                                                  />
                                                  <div className="flex-1">
                                                    <p className="text-xs text-muted-foreground truncate" title={semanticData.mainImage.url}>
                                                      {semanticData.mainImage.url}
                                                    </p>
                                                    {semanticData.mainImage.alt && (
                                                      <p className="text-xs text-muted-foreground truncate" title={semanticData.mainImage.alt}>
                                                        Alt: {semanticData.mainImage.alt}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            
                                            {semanticData.additionalImages && semanticData.additionalImages.length > 0 && (
                                              <div className="space-y-2">
                                                <span className="text-xs text-muted-foreground">Additional Images ({semanticData.additionalImages.length}):</span>
                                                <div className="grid grid-cols-2 gap-2">
                                                  {semanticData.additionalImages.map((img: any, idx: number) => (
                                                    <div key={idx} className="space-y-1">
                                                      <div className="flex items-center gap-1">
                                                        {img.context && (
                                                          <Badge variant="outline" className="text-xs">
                                                            {img.context}
                                                          </Badge>
                                                        )}
                                                        {img.isProductImage && (
                                                          <Badge variant="default" className="text-xs">Product</Badge>
                                                        )}
                                                      </div>
                                                      <img 
                                                        src={img.url} 
                                                        alt={img.alt || 'Additional image'} 
                                                        className="w-full h-16 object-cover rounded border"
                                                        onError={(e) => {
                                                          e.currentTarget.style.display = 'none';
                                                        }}
                                                      />
                                                      {img.alt && (
                                                        <p className="text-xs text-muted-foreground truncate" title={img.alt}>
                                                          {img.alt}
                                                        </p>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Comparable Fields */}
                                      {semanticData.comparableFields && semanticData.comparableFields.length > 0 && (
                                        <div className="space-y-2">
                                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Comparable Fields</h5>
                                          <div className="flex flex-wrap gap-1">
                                            {semanticData.comparableFields.map((field: string, idx: number) => (
                                              <Badge key={idx} variant="outline" className="text-xs">
                                                {field}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Topics */}
                                  {semanticData.topics && semanticData.topics.length > 0 && (
                                    <div>
                                      <h4 className="font-medium text-sm mb-2">Topics</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {semanticData.topics.map((topic: any, idx: number) => (
                                          <div key={idx} className="flex items-center gap-1">
                                            <Badge variant="default" className="text-xs">
                                              {topic.name || topic}
                                            </Badge>
                                            {topic.relevance && (
                                              <span className="text-xs text-muted-foreground">
                                                ({(topic.relevance * 100).toFixed(0)}%)
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* User Intents */}
                                  {semanticData.intents && semanticData.intents.length > 0 && (
                                    <div>
                                      <h4 className="font-medium text-sm mb-2">User Intents</h4>
                                      <div className="space-y-2">
                                        {semanticData.intents.map((intent: any, idx: number) => (
                                          <div key={idx} className="rounded-md bg-muted p-3">
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium text-sm capitalize">
                                                {(() => {
                                                  if (typeof intent === 'string') return intent;
                                                  if (intent.intent) return intent.intent;
                                                  if (intent.pageType) return intent.pageType;
                                                  if (intent.topics) {
                                                    if (Array.isArray(intent.topics)) {
                                                      return 'Topics: ' + intent.topics.join(', ');
                                                    } else if (typeof intent.topics === 'object') {
                                                      return 'Topics: ' + JSON.stringify(intent.topics);
                                                    } else {
                                                      return 'Topics: ' + String(intent.topics);
                                                    }
                                                  }
                                                  // If intent is an object with topics, entities, pageType but none of the above matched
                                                  if (typeof intent === 'object') {
                                                    return JSON.stringify(intent);
                                                  }
                                                  return 'Unknown Intent';
                                                })()}
                                              </span>
                                              {intent.confidence && (
                                                <span className="text-xs text-muted-foreground">
                                                  {(intent.confidence * 100).toFixed(0)}% confidence
                                                </span>
                                              )}
                                            </div>
                                            {intent.evidence && (
                                              <p className="text-xs text-muted-foreground mt-1">
                                                {typeof intent.evidence === 'string' ? intent.evidence : JSON.stringify(intent.evidence)}
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Metadata */}
                                  {semanticData.metadata && Object.keys(semanticData.metadata).length > 0 && (
                                    <div>
                                      <h4 className="font-medium text-sm mb-2">Page Features</h4>
                                      <div className="grid gap-2 md:grid-cols-2">
                                        {Object.entries(semanticData.metadata).map(([key, value], idx) => (
                                          <div key={idx} className="flex items-center justify-between rounded-md bg-muted p-2">
                                            <span className="text-sm">
                                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </span>
                                            <Badge variant={value ? "default" : "secondary"} className="text-xs">
                                              {String(value)}
                                            </Badge>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Analysis details */}
                                  {semanticData.analysis && (
                                    <div>
                                      <h4 className="font-medium text-sm mb-2">Detailed Analysis</h4>
                                      <div className="rounded-md bg-muted p-4">
                                        <p className="text-sm whitespace-pre-wrap">
                                          {typeof semanticData.analysis === 'string' 
                                            ? semanticData.analysis 
                                            : JSON.stringify(semanticData.analysis, null, 2)}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Raw data fallback */}
                                  {!semanticData.oneLiner && !semanticData.topics && !semanticData.intents && !semanticData.pageRole && (
                                    <div className="rounded-md bg-muted p-4">
                                      <pre className="text-sm whitespace-pre-wrap break-words">
                                        {JSON.stringify(semanticData, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  
                                  {/* Always show raw JSON in collapsible section */}
                                  <div className="pt-4 border-t">
                                    <details className="group">
                                      <summary className="cursor-pointer list-none">
                                        <div className="flex items-center gap-2 text-sm font-medium hover:text-primary">
                                          <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          View Raw JSON Data
                                        </div>
                                      </summary>
                                      <div className="mt-3">
                                        <div className="rounded-md bg-muted p-4 overflow-x-auto">
                                          <pre className="text-xs whitespace-pre font-mono">
                                            {JSON.stringify(semanticData, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </details>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">No semantic analysis data available</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="links" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Main Anchors</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {selectedPage.main_anchors && Array.isArray(selectedPage.main_anchors) && selectedPage.main_anchors.length > 0 ? (
                            <div className="space-y-2">
                              {selectedPage.main_anchors.map((anchor: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex-1 mr-2">
                                    <div className="truncate max-w-[300px]" title={anchor.linkText || anchor.text || 'No text'}>
                                      {anchor.linkText || anchor.text || 'No text'}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate" title={anchor.url || anchor.href || ''}>
                                      {anchor.url || anchor.href || ''}
                                    </div>
                                  </div>
                                  <Badge variant="outline">
                                    {anchor.semanticValue !== undefined ? anchor.semanticValue : (anchor.count || 0)}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted-foreground">No anchor data available</p>
                          )}
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Headers</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {selectedPage.headers && (Array.isArray(selectedPage.headers) ? selectedPage.headers.length > 0 : Object.keys(selectedPage.headers).length > 0) ? (
                            <div className="space-y-2">
                              {Array.isArray(selectedPage.headers) ? (
                                selectedPage.headers && Array.isArray(selectedPage.headers) && selectedPage.headers.map((header: any, idx: number) => (
                                  <div key={idx} className="text-sm" style={{ paddingLeft: `${((header.level || 1) - 1) * 12}px` }}>
                                    <span className="font-medium">{(header.tag || 'H?').toUpperCase()}:</span> {header.text || 'No text'}
                                  </div>
                                ))
                              ) : (
                                <p className="text-muted-foreground text-xs">Headers data is not in expected format</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-muted-foreground">No header data available</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="history" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Analysis History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedPage.analysis_history && Array.isArray(selectedPage.analysis_history) && selectedPage.analysis_history.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Triggered By</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Duration</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedPage.analysis_history && Array.isArray(selectedPage.analysis_history) && selectedPage.analysis_history.map((history) => (
                                <TableRow key={history.id}>
                                  <TableCell className="text-sm">
                                    {new Date(history.created_at).toLocaleString()}
                                  </TableCell>
                                  <TableCell>{history.analysis_type}</TableCell>
                                  <TableCell>{history.triggered_by}</TableCell>
                                  <TableCell>
                                    <Badge variant={history.status === 'completed' ? 'default' : history.status === 'failed' ? 'destructive' : 'outline'}>
                                      {history.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {history.duration_ms ? `${history.duration_ms}ms` : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-muted-foreground">No analysis history available</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </ScrollArea>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                    Close
                  </Button>
                  <Button onClick={() => {
                    triggerAnalysis(selectedPage.id);
                    setDetailsOpen(false);
                  }}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh Analysis
                  </Button>
                </div>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Page</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this page? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {pageToDelete && (
            <div className="space-y-2">
              <div className="rounded-md bg-muted p-3">
                <p className="text-sm font-medium">{pageToDelete.title || 'Untitled Page'}</p>
                <p className="text-xs text-muted-foreground">{pageToDelete.url}</p>
              </div>
              
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This will permanently delete the page and all its associated data including semantic analysis, content, and history.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setPageToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePage}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Page'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reprocessing Progress Dialog */}
      <ReprocessingDialog
        open={reprocessingDialogOpen}
        onOpenChange={setReprocessingDialogOpen}
        siteId={siteId}
        selectedPageIds={reprocessingPageIds}
        totalPages={reprocessingPageIds.length}
        onComplete={handleReprocessComplete}
      />
      
      <AddUrlDialog
        open={addUrlDialogOpen}
        onOpenChange={setAddUrlDialogOpen}
        siteId={siteId}
        siteUrl={siteUrl}
        onSuccess={fetchPages}
      />
    </div>
  );
};